import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../shared/components/ui/Button';
import ResponsiveFormLayout from '../../../shared/components/layout/ResponsiveFormLayout';
import { useAuth, useAuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import { useLockoutCountdown, formatCountdown } from '../../../hooks/useLockoutCountdown';
import { retryAfterOf, waitKeys } from '../../../shared/utils/authErrors';

interface LoginScreenProps {
  onNext: () => void;
  onError: (error: string) => void;
}

const LoginScreen = ({ onNext, onError }: LoginScreenProps) => {
  const [licenseNumber, setLicenseNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneLast4, setPhoneLast4] = useState<string | null>(null);
  const [step, setStep] = useState<'enter_license' | 'confirm_phone'>('enter_license');
  // Lockout countdown driven by the backend's `retry_after` (rate limit)
  const { secondsLeft: lockSeconds, isLocked, start: startLockout } = useLockoutCountdown();
  const sendingOtpRef = useRef(false);

  const authService = useAuth();
  const { t } = useLanguage();
  const { isAuthenticated, isLoading } = useAuthContext();
  const navigate = useNavigate();

  // Redirect if already authenticated - let the protected routes handle proper routing
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      console.log('🔄 Already authenticated, redirecting to home');
      navigate('/home');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const validateLicenseNumber = (license: string): boolean => {
    // License number should not be empty and should be numeric
    return license.length > 0 && /^\d+$/.test(license);
  };

  const handleGetPhone = async () => {
    if (!licenseNumber) {
      setError(t('enter_license_number'));
      return;
    }

    if (!validateLicenseNumber(licenseNumber)) {
      setError(t('invalid_license_number'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await authService.getPhoneByLicense(licenseNumber);
      if (result.isSuccess && result.data) {
        // DMIS-unavailable fallback: doctor not found or license expired → scan ID QR.
        if (result.data.action === 'qr_required') {
          sessionStorage.setItem('login_license_number', licenseNumber);
          sessionStorage.setItem('qr_reason', result.data.reason || '');
          navigate('/scan_id');
          return;
        }
        setPhoneLast4(result.data.phone_last_4_digits);
        setStep('confirm_phone');
      } else {
        // API error will be shown globally via ErrorNotification
        // No need to show local error
        onError(result.error || 'Failed to get phone number');
      }
    } catch (err) {
      // Exception error will be shown globally
      const errorMessage = err instanceof Error ? err.message : 'Failed to get phone number';
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAndSendOTP = async () => {
    if (isLocked) return;
    // Synchronous re-entry guard: `loading` state updates async, so a fast
    // double-tap can fire two send-OTP requests before the button disables.
    if (sendingOtpRef.current) return;
    sendingOtpRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await authService.sendOtpByLicense(licenseNumber);
      if (result.isSuccess) {
        // Store license number for OTP verification
        sessionStorage.setItem('login_license_number', licenseNumber);
        onNext();
      } else {
        // Rate-limited (429 otp.rate_limited): a live countdown instead of the server's sentence,
        // whose seconds go stale.
        const retryAfter = retryAfterOf(result.errorDetails);
        if (retryAfter > 0) {
          startLockout(retryAfter);
          setError(null);
        } else {
          // API error will be shown globally via ErrorNotification
          onError(result.error || 'Failed to send OTP');
        }
      }
    } catch (err) {
      // Exception error will be shown globally
      const errorMessage = err instanceof Error ? err.message : 'Failed to send OTP';
      onError(errorMessage);
    } finally {
      sendingOtpRef.current = false;
      setLoading(false);
    }
  };

  const handleLicenseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setLicenseNumber(value);
    setError(null);
  };

  const handleBack = () => {
    setStep('enter_license');
    setPhoneLast4(null);
    setError(null);
  };

  // Step 1: Enter license number
  if (step === 'enter_license') {
    const continueButton = (
      <Button
        onClick={handleGetPhone}
        text={t('continue')}
        loading={loading}
        disabled={!licenseNumber || loading}
      />
    );

    return (
      <ResponsiveFormLayout
        showBackButton={false}
        showLanguageToggle={true}
        icon="/ima_logo.png"
        showIcon={true}
        iconSize="w-40 h-32"
        title={t('platform_title')}
        subtitle={t('platform_description')}
        actions={continueButton}
        maxHeight="95vh"
      >
        <form onSubmit={(e) => { e.preventDefault(); if (licenseNumber && !loading) handleGetPhone(); }} className="w-full">
          {/* Enter license instruction */}
          <div className="text-center mb-4">
            <div className="text-[#726c6c] text-base font-medium">
              {t('enter_license_number')}
            </div>
          </div>

          {/* License Number Input */}
          <div className="w-full mb-8">
            <input
              type="text"
              inputMode="numeric"
              value={licenseNumber}
              onChange={handleLicenseChange}
              placeholder={t('license_number')}
              className={`w-full px-4 py-3 text-lg border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#2e5d7d] ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              dir="ltr"
            />
            {error && (
              <div className="text-red-500 text-sm mt-2 text-center">{error}</div>
            )}
          </div>

          {/* Privacy Policy Section */}
          <div className="w-full space-y-4 text-center">
            <div className="text-[#726c6c] text-xs font-normal leading-relaxed">
              <div className="font-medium mb-2">{t('privacy_policy')}</div>
              <div className="leading-relaxed">{t('privacy_text')}</div>
            </div>

            {/* Terms text */}
            <div className="text-[#726c6c] text-xs font-normal leading-tight">
              {t('terms_text')}
            </div>
          </div>
        </form>
      </ResponsiveFormLayout>
    );
  }

  // Step 2: Confirm phone and send OTP
  const confirmButton = (
    <div className="w-full space-y-3">
      {isLocked && (
        <div className="w-full rounded-2xl border-2 border-red-200 bg-red-50 px-4 py-3 text-center">
          <div className="text-sm font-semibold text-red-600">
            {t(waitKeys('send').title)}
          </div>
          <div className="mt-1 text-xs text-red-500">
            {t(waitKeys('send').body)}
          </div>
          <div className="mt-1 font-mono text-2xl font-bold text-red-600" dir="ltr">
            {formatCountdown(lockSeconds)}
          </div>
        </div>
      )}
      <Button
        onClick={handleConfirmAndSendOTP}
        text={t('confirm_and_send_otp')}
        loading={loading}
        disabled={loading || isLocked}
      />
    </div>
  );

  return (
    <ResponsiveFormLayout
      showBackButton={true}
      onBack={handleBack}
      showLanguageToggle={true}
      icon="/ima_logo.png"
      showIcon={true}
      iconSize="w-40 h-32"
      title={t('confirm_phone_number')}
      subtitle={t('we_will_send_otp')}
      actions={confirmButton}
      maxHeight="95vh"
    >
      <div className="w-full">
        {/* Phone number display */}
        <div className="text-center mb-8">
          <div className="text-[#726c6c] text-base font-medium mb-4">
            {t('phone_ending_with')}
          </div>
          <div className="text-[#2e5d7d] text-3xl font-bold" dir="ltr">
            ***{phoneLast4}
          </div>
        </div>

        {error && (
          <div className="text-red-500 text-sm text-center mb-4">{error}</div>
        )}

        {/* Info text */}
        <div className="w-full text-center text-[#726c6c] text-sm font-normal leading-relaxed">
          {t('otp_will_be_sent_to_phone')}
        </div>
      </div>
    </ResponsiveFormLayout>
  );
};

export default LoginScreen;